import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  CalendarDays,
  Check,
  ChevronsUpDown,
  Disc3,
  Library,
  FileSpreadsheet,
  LifeBuoy,
  LayoutDashboard,
  ListMusic,
  LogOut,
  Menu,
  Mic,
  Search,
  Settings as SettingsIcon,
  ShieldCheck,
  UserRound,
  Users,
  UsersRound,
  X,
  CircleDollarSign,
  Wallet,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useTenant } from "@/context/TenantContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { SUPPORT_CHANGED, onChange } from "@/lib/events";
import { noteVisit } from "@/lib/lastPage";
import { prefetchWorkspace } from "@/lib/prefetch";
import { WriterSearch } from "@/pages/Writer";
import { cn } from "@/lib/utils";

// `group` starts a headed section; items after it carry `in` so the section
// can be folded. Overview and Catalog cover both sides, so they sit above.
const NAV = [
  { to: "/overview", label: "Overview", icon: LayoutDashboard },
  { to: "/catalog", label: "Catalog", icon: ListMusic },
  { to: "/albums", label: "Albums", icon: Library, editor: true, sub: true },
  { group: "recording", label: "Recording", tone: "text-sec-release" },
  { to: "/production", label: "Production", icon: Mic, editor: true, in: "recording" },
  { to: "/releases", label: "Releases", icon: CalendarDays, in: "recording" },
  { group: "publishing", label: "Publishing", tone: "text-sec-pub" },
  { to: "/writers", label: "Writers", icon: Users, in: "publishing" },
  { to: "/registrations", label: "Registrations", icon: ShieldCheck, in: "publishing" },
  { to: "/royalties", label: "Royalties", icon: CircleDollarSign, in: "publishing" },
  { to: "/payouts", label: "Payouts", icon: Wallet, in: "publishing" },
  { group: "workspace", label: "Workspace", tone: "text-muted2", fixed: true },
  { to: "/import", label: "Import & export", icon: FileSpreadsheet, editor: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, admin: true },
  { to: "/team", label: "Team", icon: UsersRound, admin: true },
  { to: "/support", label: "Support", icon: LifeBuoy, badge: "mine" },
  { to: "/profile", label: "Your profile", icon: UserRound },
];

const FOLD_KEY = "nav_folded";
function readFolded() {
  try {
    return JSON.parse(window.localStorage.getItem(FOLD_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

// A writer login's whole app — see pages/Writer.jsx.
const WRITER_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/my-songs", label: "My songs", icon: ListMusic },
  { to: "/royalties", label: "Royalties", icon: CircleDollarSign },
  { to: "/payouts", label: "Payouts", icon: Wallet },
  { group: "workspace", label: "", tone: "text-muted2", fixed: true },
  { to: "/support", label: "Support", icon: LifeBuoy, badge: "mine" },
  { to: "/profile", label: "Your profile", icon: UserRound },
];

const OWNER_NAV = [
  { to: "/platform", label: "Overview", icon: LayoutDashboard, exact: true },
  { to: "/workspaces", label: "Accounts", icon: Building2 },
  { to: "/platform/people", label: "People", icon: UsersRound },
  { to: "/support-inbox", label: "Support", icon: LifeBuoy, badge: "owner" },
  { to: "/platform/settings", label: "Platform settings", icon: SettingsIcon },
  { to: "/profile", label: "Your profile", icon: UserRound },
];

/**
 * Two counts in one place, as in FreelanceHub: filled = waiting on you,
 * outlined = already in hand.
 */
function NavBadge({ action = 0, pending = 0 }) {
  if (!action && !pending) return null;
  return (
    <span className="ml-auto flex items-center gap-1 shrink-0">
      {action > 0 && (
        <span title={`${action} waiting on a reply`} className="bg-brand text-white text-[10.5px] font-mono rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
          {action > 9 ? "9+" : action}
        </span>
      )}
      {pending > 0 && (
        <span title={`${pending} in progress`} className="border border-line text-muted2 text-[10.5px] font-mono rounded-full px-1.5 py-[1px] min-w-[18px] text-center">
          {pending > 9 ? "9+" : pending}
        </span>
      )}
    </span>
  );
}

/** "Open a client" — jump straight into any workspace from the owner console. */
function OpenWorkspaceList({ onNavigate }) {
  const { tenants, setTenantId } = useTenant();
  const navigate = useNavigate();
  if (!tenants.length) return null;
  return (
    <div className="mt-2 pt-3 border-t border-line">
      <div className="eyebrow px-3.5 pb-2 text-[10.5px]">Open a workspace</div>
      {tenants.map((t) => (
        <button
          key={t.tenant_id}
          onClick={() => {
            setTenantId(t.tenant_id);
            onNavigate?.();
            navigate("/overview");
          }}
          className="w-full flex items-center gap-3 px-3.5 h-9 rounded-xl text-[13px] text-muted2 hover:bg-brand-light/50 hover:text-ink text-left"
        >
          <span className="font-mono text-[10.5px] font-semibold text-gold w-5 shrink-0">
            {t.name
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((w) => w[0])
              .join("")
              .toUpperCase()}
          </span>
          <span className="truncate">{t.name}</span>
          {t.active === false && <span className="ml-auto text-[10.5px] text-gold">Paused</span>}
        </button>
      ))}
    </div>
  );
}

function PlatformBadge() {
  const [name, setName] = useState(() => {
    try {
      return window.localStorage.getItem("platform_name") || "MetaTrack";
    } catch {
      return "MetaTrack";
    }
  });
  useEffect(() => {
    api
      .get("/meta")
      .then(({ data }) => {
        setName(data.platform_name);
        try {
          window.localStorage.setItem("platform_name", data.platform_name);
        } catch {
          /* private browsing */
        }
      })
      .catch(() => {});
  }, []);
  return (
    <div className="flex items-center gap-2.5 w-full">
      <div className="w-9 h-9 rounded-xl bg-ink flex items-center justify-center shrink-0">
        <Building2 className="w-[18px] h-[18px] text-white" />
      </div>
      <div className="min-w-0">
        <div className="font-display text-[15.5px] font-semibold text-ink leading-tight truncate">{name}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted2 mt-1">Owner</div>
      </div>
    </div>
  );
}

function WorkspaceBadge({ onNavigate }) {
  const { isOwner, tenants, tenantId, setTenantId, activeTenant } = useTenant();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const name = settings.publisher_name || activeTenant?.name || "Workspace";
  const sub = settings.publisher_ipi ? `${settings.publisher_pro} · IPI ${settings.publisher_ipi}` : "Publisher";

  const badge = (
    <>
      <div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center shrink-0 shadow-sm">
        <Disc3 className="w-[18px] h-[18px] text-white" />
      </div>
      <div className="min-w-0 flex-1 text-left">
        <div className="font-display text-[15.5px] font-semibold tracking-[-0.01em] text-ink leading-tight truncate">{name}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted2 mt-1 truncate">{sub}</div>
      </div>
    </>
  );

  if (!isOwner) return <div className="flex items-center gap-2.5 w-full">{badge}</div>;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-2.5 w-full rounded-xl p-1 -m-1 hover:bg-brand-light/60 transition-colors" aria-label="Switch workspace">
          {badge}
          <ChevronsUpDown className="w-4 h-4 text-muted2 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <div className="px-3 py-2 border-b border-line">
          <div className="eyebrow">Switch workspace</div>
        </div>
        <div className="max-h-[260px] overflow-y-auto py-1">
          {tenants.map((t) => (
            <button
              key={t.tenant_id}
              onClick={() => {
                setTenantId(t.tenant_id);
                setOpen(false);
                onNavigate?.();
                navigate("/overview");
              }}
              className={cn("w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-brand-light/60", t.tenant_id === tenantId && "bg-brand-light")}
            >
              <Check className={cn("w-4 h-4 text-brand shrink-0", t.tenant_id === tenantId ? "opacity-100" : "opacity-0")} />
              <span className="text-[13px] text-ink truncate flex-1">{t.name}</span>
              <span className="font-mono text-[11px] text-muted2">{t.track_count}</span>
            </button>
          ))}
        </div>
        <div className="border-t border-line p-1">
          <Link
            to="/platform"
            onClick={() => {
              setOpen(false);
              onNavigate?.();
            }}
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-muted2 hover:bg-brand-light/60 hover:text-ink"
          >
            <Building2 className="w-4 h-4" /> Platform console
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState({ tracks: [], writers: [] });
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setRes({ tracks: [], writers: [] });
      return undefined;
    }
    timer.current = setTimeout(() => {
      api
        .get("/search", { params: { q } })
        .then(({ data }) => setRes(data))
        .catch(() => {});
    }, 180);
    return () => clearTimeout(timer.current);
  }, [q]);

  const go = (path) => {
    setOpen(false);
    setQ("");
    navigate(path);
  };
  const has = res.tracks.length || res.writers.length;

  return (
    <Popover open={open && q.trim().length >= 2} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <label className="hidden md:flex items-center gap-2 h-9 w-[280px] xl:w-[320px] px-3 rounded-lg border border-line bg-card text-muted2 focus-within:border-brand-ring">
          <Search className="w-4 h-4 shrink-0" />
          <input
            aria-label="Search the catalog"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && q.trim()) go(`/catalog?q=${encodeURIComponent(q.trim())}`);
            }}
            placeholder="Search tracks, ISRC, UPC, writers…"
            className="bg-transparent outline-none text-[13px] text-ink w-full placeholder:text-muted2"
          />
        </label>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-1" onOpenAutoFocus={(e) => e.preventDefault()}>
        {!has && <div className="px-3 py-4 text-[13px] text-muted2">Nothing matches “{q}”.</div>}
        {res.tracks.length > 0 && <div className="eyebrow px-3 pt-2 pb-1">Tracks</div>}
        {res.tracks.map((t) => (
          <button key={t.track_id} onClick={() => go(`/catalog/${t.track_id}`)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-brand-light/60">
            <div className="text-[13px] font-medium text-ink">{t.title}</div>
            <div className="text-[11.5px] text-muted2">
              {t.artist} {t.isrc && <span className="font-mono">· {t.isrc}</span>}
            </div>
          </button>
        ))}
        {res.writers.length > 0 && <div className="eyebrow px-3 pt-2 pb-1">Writers</div>}
        {res.writers.map((w) => (
          <button key={w.writer_id} onClick={() => go(`/writers?open=${w.writer_id}`)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-brand-light/60">
            <div className="text-[13px] font-medium text-ink">{w.name}</div>
            <div className="text-[11.5px] text-muted2">{[w.stage_name, w.ipi && `IPI ${w.ipi}`].filter(Boolean).join(" · ")}</div>
          </button>
        ))}
        {q.trim() && (
          <button onClick={() => go(`/catalog?q=${encodeURIComponent(q.trim())}`)} className="w-full text-left px-3 py-2 mt-1 border-t border-line text-[12.5px] text-brand font-medium">
            See all results in the catalog
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function Layout({ children, title, subtitle, action, owner = false }) {
  const { user, logout } = useAuth();
  const { setTenantId } = useTenant();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const role = user?.role;
  const canAdmin = ["owner", "admin"].includes(role);
  const canEdit = role !== "writer";

  const [support, setSupport] = useState({ action: 0, pending: 0 });

  // Remember the page, so a support message can suggest what it's about.
  const { tenantId: activeTenantId } = useTenant();
  useEffect(() => {
    if (!owner) prefetchWorkspace(activeTenantId, role);
  }, [owner, activeTenantId, role]);

  useEffect(() => {
    noteVisit(pathname);
  }, [pathname]);

  // A reply is easy to miss, so it shows as a count on the Support tab.
  useEffect(() => {
    let cancelled = false;
    const check = () =>
      (owner ? api.get("/support/summary") : api.get("/support/mine/summary"))
        .then(({ data }) => {
          if (cancelled) return;
          if (owner) setSupport({ action: data.new || 0, pending: data.working || 0 });
          else setSupport({ action: data.unread || 0, pending: Math.max((data.open || 0) - (data.unread || 0), 0) });
        })
        .catch(() => {});
    check();
    const timer = setInterval(check, 30000);
    const off = onChange(SUPPORT_CHANGED, check);
    window.addEventListener("focus", check);
    return () => {
      cancelled = true;
      clearInterval(timer);
      off();
      window.removeEventListener("focus", check);
    };
  }, [owner, pathname]);

  const items = owner ? OWNER_NAV : role === "writer" ? WRITER_NAV : NAV.filter((n) => (!n.admin || canAdmin) && (!n.editor || canEdit));
  const isActive = (n) => pathname === n.to || (!n.exact && pathname.startsWith(`${n.to}/`));
  const [folded, setFolded] = useState(readFolded);
  const toggleGroup = (key) =>
    setFolded((f) => {
      const next = { ...f, [key]: !f[key] };
      try {
        window.localStorage.setItem(FOLD_KEY, JSON.stringify(next));
      } catch {
        /* private browsing */
      }
      return next;
    });
  // A folded group still shows the page you're on, so you never lose your place.
  const hidden = (n) => n.in && folded[n.in] && !isActive(n);

  const doLogout = async () => {
    setTenantId(null);
    await logout();
    navigate("/login");
  };

  const sidebar = (
    <>
      <div className="h-[68px] flex items-center px-4 border-b border-line shrink-0">
        {owner ? <PlatformBadge /> : <WorkspaceBadge onNavigate={() => setOpen(false)} />}
      </div>
      <nav className="flex-1 p-3 flex flex-col gap-0.5 overflow-y-auto" aria-label="Main">
        {items.map((n) =>
          n.group ? (
            n.fixed ? (
              // mt-auto pins this section to the bottom, so it stays put when the groups above fold
              <div key={n.group} className={cn("mt-auto flex items-center gap-2 px-3.5 pt-4 pb-1.5 font-mono text-[10.5px] tracking-[0.14em] uppercase", n.tone)}>
                {n.label}
                <span className="flex-1 h-px bg-line" />
              </div>
            ) : (
              <button
                key={n.group}
                type="button"
                onClick={() => toggleGroup(n.group)}
                aria-expanded={!folded[n.group]}
                className={cn("w-full flex items-center gap-2 px-3.5 pt-4 pb-1.5 font-mono text-[10.5px] tracking-[0.14em] uppercase text-left rounded-lg", n.tone)}
              >
                {n.label}
                <span className="flex-1 h-px bg-line" />
                <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", folded[n.group] && "-rotate-90")} />
              </button>
            )
          ) : hidden(n) ? null : (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              aria-current={isActive(n) ? "page" : undefined}
              className={cn(
                "flex items-center gap-3 px-3.5 h-10 rounded-xl text-[13.5px] font-medium transition-colors",
                n.sub && "ml-5 h-9",
                isActive(n) ? "bg-brand-light text-brand" : "text-muted2 hover:bg-brand-light/50 hover:text-ink"
              )}
            >
              <n.icon className="w-[17px] h-[17px] shrink-0" />
              {n.label}
              {n.badge && <NavBadge {...support} />}
            </Link>
          )
        )}
        {owner && <OpenWorkspaceList onNavigate={() => setOpen(false)} />}
      </nav>
      <div className="p-3 border-t border-line shrink-0">
        {role === "owner" && !owner && (
          <Link
            to="/platform"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-3.5 h-10 mb-1 rounded-xl text-[13.5px] font-medium text-muted2 hover:bg-brand-light/50 hover:text-ink transition-colors"
          >
            <Building2 className="w-[17px] h-[17px]" /> Platform console
          </Link>
        )}
        <div className="flex items-center gap-3 px-2.5 py-2">
          <Link to="/profile" onClick={() => setOpen(false)} title="Your profile" className="w-9 h-9 rounded-full bg-brand-light flex items-center justify-center font-display font-semibold text-brand text-[13px] shrink-0 hover:ring-2 hover:ring-brand-ring">
            {(user?.name?.[0] || "U").toUpperCase()}
          </Link>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium text-ink truncate">{user?.name}</div>
            <div className="text-[11.5px] text-muted2">{role === "owner" ? "Platform owner" : role.charAt(0).toUpperCase() + role.slice(1)}</div>
          </div>
          <button onClick={doLogout} title="Sign out" aria-label="Sign out" className="text-muted2 hover:text-stock-out p-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/40">
            <LogOut className="w-[17px] h-[17px]" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-surface">
      <aside className="hidden lg:flex w-[248px] bg-card border-r border-line flex-col fixed h-screen z-30">{sidebar}</aside>
      {open && (
        <>
          <div className="lg:hidden fixed inset-0 bg-ink/30 backdrop-blur-sm z-40" onClick={() => setOpen(false)} />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-[264px] bg-card border-r border-line flex flex-col z-50 animate-rise">
            <button onClick={() => setOpen(false)} aria-label="Close menu" className="absolute right-2 top-5 p-1.5 rounded-lg text-muted2">
              <X className="w-4 h-4" />
            </button>
            {sidebar}
          </aside>
        </>
      )}
      <div className="lg:ml-[248px] min-w-0">
        <header className="sticky top-0 z-20 h-[68px] bg-surface/95 backdrop-blur border-b border-line flex items-center gap-3 px-4 sm:px-7">
          <button onClick={() => setOpen(true)} aria-label="Open menu" className="lg:hidden -ml-1 p-2 rounded-lg text-ink hover:bg-card">
            <Menu className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="page-title truncate text-[22px] sm:text-[24px]">{title}</h1>
            {subtitle && <div className="text-[12.5px] text-muted2 truncate mt-0.5">{subtitle}</div>}
          </div>
          {!owner && (role === "writer" ? <WriterSearch /> : <GlobalSearch />)}
          {action && <div className="flex items-center gap-2 shrink-0">{action}</div>}
        </header>
        <main className="px-4 sm:px-7 py-6 pb-16 min-w-0">
          <div className="mx-auto w-full max-w-[1500px]">{children}</div>
        </main>
      </div>
    </div>
  );
}
